import { useUnit } from "effector-react";
import { useEffect, useState, useCallback } from "react";
import {
  $partnersStore,
  fetchPartnersRequested,
  loadMorePartnersRequested,
  fetchPartnersGeneralDataRequested,
} from "../store";
import { PartnerResponse, PartnerFilters, Member } from "../model/types";
import { partnersService } from "../service/api";

export const usePartnersViewModel = () => {
  const [
    partnersState,
    fetchPartners,
    loadMorePartners,
    fetchPartnersGeneralData,
  ] = useUnit([
    $partnersStore,
    fetchPartnersRequested,
    loadMorePartnersRequested,
    fetchPartnersGeneralDataRequested,
  ]);

  const [filteredPartners, setFilteredPartners] = useState<PartnerResponse[]>(
    [],
  );
  const [filteredUsers, setFilteredUsers] = useState<Member[]>([]);
  const [currentFilter, setCurrentFilter] = useState<
    "all" | "followed" | "my_network"
  >("all");
  const [filterLoading, setFilterLoading] = useState(false);
  const [loadMoreUsersLoading, setLoadMoreUsersLoading] = useState(false);
  const [networkFollowers, setNetworkFollowers] = useState<Member[]>([]);
  const [networkFollowing, setNetworkFollowing] = useState<Member[]>([]);
  const [followersCount, setFollowersCount] = useState<number>(0);
  const [followingCount, setFollowingCount] = useState<number>(0);
  const [usersPagination, setUsersPagination] = useState<{
    currentPage: number;
    totalCount: number;
    count: number;
    hasMore: boolean;
  }>({
    currentPage: 1,
    totalCount: 0,
    count: 0,
    hasMore: false,
  });

  const fetchPartnersWithFilters = (filters?: PartnerFilters) => {
    // console.log(filters);
    fetchPartners(filters);
  };

  useEffect(() => {
    // make a general call once to get tab counts

    getPartnersGeneralData();
  }, [fetchPartnersGeneralData]);

  const fetchAllUsers = async () => {
    setFilterLoading(true);
    try {
      const response = await partnersService.getMyNetworkUsers(1);
      setFilteredUsers(response.data || []);
      setUsersPagination({
        currentPage: 1,
        totalCount: response.total_count || 0,
        count: response.count || 0,
        hasMore: (response.data?.length || 0) < (response.count || 0),
      });
    } catch (error) {
      console.error("Error fetching all users:", error);
      setFilteredUsers([]);
      setUsersPagination({
        currentPage: 1,
        totalCount: 0,
        count: 0,
        hasMore: false,
      });
    } finally {
      setFilterLoading(false);
    }
  };

  useEffect(() => {
    if (currentFilter === "all" && partnersState.partners.length > 0) {
      setFilteredPartners(partnersState.partners);
    }
  }, [partnersState.partners, currentFilter]);

  const fetchFilteredData = useCallback(
    async (filter: "all" | "followed" | "my_network") => {
      setFilterLoading(true);
      setCurrentFilter(filter);

      try {
        switch (filter) {
          case "all":
            fetchPartners(undefined);
            setFilteredUsers([]);
            break;
          case "followed":
            // Fetch both followers and following when "My Network" is selected
            const [followersResponse, followingResponse] = await Promise.all([
              partnersService.getNetworkFollowers(),
              partnersService.getNetworkFollowing(),
            ]);
            setNetworkFollowers(followersResponse.data || []);
            setNetworkFollowing(followingResponse.data || []);
            setFollowersCount(followersResponse.count || 0);
            setFollowingCount(followingResponse.count || 0);
            // Default to showing followers
            setFilteredUsers(followersResponse.data || []);
            setFilteredPartners([]);
            break;
          case "my_network":
            const networkResponse = await partnersService.getMyNetworkUsers(1);
            setFilteredUsers(networkResponse.data || []);
            setFilteredPartners([]);
            setUsersPagination({
              currentPage: 1,
              count: networkResponse.count,
              totalCount: networkResponse.total_count || 0,
              hasMore:
                (networkResponse.data?.length || 0) <
                (networkResponse.count || 0),
            });
            break;
          default:
            setFilteredPartners(partnersState.partners);
            setFilteredUsers([]);
        }
      } catch (error) {
        console.error("Error fetching filtered data:", error);
        setFilteredPartners([]);
        setFilteredUsers([]);
      } finally {
        setFilterLoading(false);
      }
    },
    [],
  );

  const fetchNetworkTabData = async (tabType: "followers" | "following") => {
    setFilterLoading(true);
    try {
      if (tabType === "followers") {
        if (networkFollowers.length === 0) {
          const followersResponse = await partnersService.getNetworkFollowers();
          setNetworkFollowers(followersResponse.data || []);
          setFollowersCount(followersResponse.total_count || 0);
          setFilteredUsers(followersResponse.data || []);
        } else {
          setFilteredUsers(networkFollowers);
        }
      } else {
        if (networkFollowing.length === 0) {
          const followingResponse = await partnersService.getNetworkFollowing();
          setNetworkFollowing(followingResponse.data || []);
          setFollowingCount(followingResponse.total_count || 0);
          setFilteredUsers(followingResponse.data || []);
        } else {
          setFilteredUsers(networkFollowing);
        }
      }
    } catch (error) {
      console.error("Error fetching network tab data:", error);
      setFilteredUsers([]);
    } finally {
      setFilterLoading(false);
    }
  };

  const getFilteredPartners = (
    filter: "all" | "followed" | "my_network",
  ): PartnerResponse[] => {
    if (currentFilter === filter) {
      return filteredPartners;
    }
    return partnersState.partners;
  };

  const getAllPartnersCount = (): number => {
    return partnersState.pagination.total_count;
  };
  const getTotalPartnersCount = (): number => {
    return partnersState.pagination.total;
  };

  const loadMorePartnersWithFilters = (filters?: PartnerFilters) => {
    loadMorePartners(filters);
  };

  const hasMorePartners = (): boolean => {
    const totalPartners = partnersState.pagination.total_count;
    const loadedPartners = partnersState.partners.length;

    return loadedPartners < totalPartners;
  };

  const searchUsers = async (searchQuery: string) => {
    setFilterLoading(true);
    try {
      const response = await partnersService.searchUsers(searchQuery, 1);
      setFilteredUsers(response.data || []);
      // setFilteredPartners([]);
      setUsersPagination({
        currentPage: 1,
        totalCount: response.total_count || 0,
        count: response.count || 0,
        hasMore: (response.data?.length || 0) < (response.count || 0),
      });
    } catch (error) {
      console.error("Error searching users:", error);
      setFilteredUsers([]);
      setUsersPagination({
        currentPage: 1,
        totalCount: 0,
        count: 0,
        hasMore: false,
      });
    } finally {
      setFilterLoading(false);
    }
  };

  const fetchUsersWithFilters = async (filters?: PartnerFilters) => {
    setFilterLoading(true);
    try {
      const response = await partnersService.getUsersWithFilters(filters);
      setFilteredUsers(response.data || []);
      setFilteredPartners([]);
      setUsersPagination({
        currentPage: 1,
        totalCount: response.total_count || 0,
        count: response.count || 0,
        hasMore: (response.data?.length || 0) < (response.count || 0),
      });
    } catch (error) {
      console.error("Error fetching users with filters:", error);
      setFilteredUsers([]);
      setUsersPagination({
        currentPage: 1,
        totalCount: 0,
        count: 0,
        hasMore: false,
      });
    } finally {
      setFilterLoading(false);
    }
  };

  const loadMoreUsers = async (filters?: PartnerFilters) => {
    if (!usersPagination.hasMore || loadMoreUsersLoading) return;

    setLoadMoreUsersLoading(true);
    try {
      const nextPage = usersPagination.currentPage + 1;
      const hasFilters =
        filters &&
        Object.values(filters).some((v) => v !== undefined && v !== "");

      const response = hasFilters
        ? await partnersService.getUsersWithFilters(filters, nextPage)
        : filters?.search
          ? await partnersService.searchUsers(filters.search, nextPage)
          : await partnersService.getMyNetworkUsers(nextPage);

      setFilteredUsers((prev) => [...prev, ...(response.data || [])]);
      setUsersPagination({
        currentPage: nextPage,
        totalCount: response.total_count || 0,
        count: response.count,
        hasMore:
          filteredUsers.length + (response.data?.length || 0) <
          (response.count || 0),
      });
    } catch (error) {
      console.error("Error loading more users:", error);
    } finally {
      setLoadMoreUsersLoading(false);
    }
  };

  const getPartnersGeneralData = async () => {
    await fetchPartnersGeneralData();
    const response = partnersState.partnersGeneralData;
    if (response) {
      // console.log("Partners General Data:", response);
    }
    return response;
  };

  return {
    partners: partnersState.partners,
    loading: partnersState.loading,
    loadMore: partnersState.loadMore,
    error: partnersState.error,
    getFilteredPartners,
    getAllPartnersCount,
    getTotalPartnersCount,
    refetch: fetchPartners,
    fetchPartnersWithFilters,
    loadMorePartnersWithFilters,
    hasMorePartners,
    pagination: partnersState.pagination,
    fetchFilteredData,
    filterLoading,
    loadMoreUsersLoading,
    currentFilter,
    filteredPartners,
    filteredUsers,
    fetchNetworkTabData,
    networkFollowers,
    networkFollowing,
    followersCount,
    followingCount,
    searchUsers,
    fetchAllUsers,
    fetchUsersWithFilters,
    loadMoreUsers,
    usersPagination,
    partnersState,
  };
};
